import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiError, apiFetch } from '../../lib/api';
import type { Round2AdminView } from '../../lib/round2';
import { SHOOTER_GENDER_LABELS, type ShooterGender } from '../../lib/shooting';
import { scoringRound2Path } from '../../lib/scoring-routes';

interface Round2SetupCardProps {
  tournamentId: string;
  slug: string;
  finished?: boolean;
}

/** Rifle scoring: Round 2 is filled from the top Round 1 teams. Admins only. */
export function Round2SetupCard({ tournamentId, slug, finished = false }: Round2SetupCardProps) {
  const [view, setView] = useState<Round2AdminView | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [switchBusy, setSwitchBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    apiFetch<{ round2: Round2AdminView }>(`/api/admin/shooting/tournaments/${tournamentId}/round2`)
      .then((body) => {
        if (!cancelled) setView(body.round2);
      })
      .catch((err: unknown) => {
        if (!cancelled) setLoadError(err instanceof ApiError ? err.message : 'Could not load Round 2.');
      });
    return () => {
      cancelled = true;
    };
  }, [tournamentId]);

  async function setPublic(next: boolean) {
    setSwitchBusy(true);
    setFormError(null);
    try {
      const body = await apiFetch<{ round2: Round2AdminView }>(
        `/api/admin/shooting/tournaments/${tournamentId}/round2/visibility`,
        { method: 'PATCH', body: JSON.stringify({ publicVisible: next }) },
      );
      setView(body.round2);
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : 'Could not change the public leaderboard.');
    } finally {
      setSwitchBusy(false);
    }
  }

  const promotion = view?.promotion;
  const tieOpen = (promotion?.slots ?? 0) > 0;

  return (
    <section className="space-y-3 rounded-[var(--radius-lg)] border border-[var(--color-border)] p-4" aria-label="Round 2">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-[var(--color-text-primary)]">Round 2</h2>
          <p className="mt-1 text-sm text-[var(--color-text-secondary)]">
            {finished ? 'Final Round 2 results stay locked. Public visibility can still be changed.' : <>{promotion ? `The top ${promotion.qualifyingTeams} Round 1 teams` : 'The qualifying Round 1 teams'} are added automatically, with the same names and members.
            Ranked by the Round 2 team score.
            {view ? ` Round 2 has ${view.existingCount} shooter${view.existingCount === 1 ? '' : 's'}.` : ''}</>}
          </p>
        </div>
        <Link
          to={scoringRound2Path(slug)}
          className="inline-flex min-h-11 items-center rounded-[var(--radius-md)] border border-[var(--color-border-strong)] px-4 text-sm font-medium text-[var(--color-text-primary)]"
        >
          Open Round 2
        </Link>
      </div>
      {!finished && tieOpen ? (
        <p className="rounded-[var(--radius-md)] bg-amber-50 px-3 py-2 text-sm text-amber-950">
          {promotion?.slots} team{promotion?.slots === 1 ? '' : 's'} still tied for the last place. Choose them on the Teams page.
        </p>
      ) : null}
      <div className="flex items-center justify-between gap-3 border-t border-[var(--color-border)] pt-3">
        <p className="text-sm text-[var(--color-text-secondary)]">Show Round 2 on public leaderboard</p>
        <button
          id="round2-public-switch"
          type="button"
          role="switch"
          aria-checked={view?.publicVisible === true}
          disabled={switchBusy || !view}
          onClick={() => void setPublic(view?.publicVisible !== true)}
          className={`relative h-8 w-14 shrink-0 rounded-full transition-colors disabled:opacity-50 ${
            view?.publicVisible ? 'bg-[var(--color-accent)]' : 'bg-[var(--color-border-strong)]'
          }`}
        >
          <span className={`absolute top-1 h-6 w-6 rounded-full bg-white transition-transform ${view?.publicVisible ? 'left-7' : 'left-1'}`} />
          <span className="sr-only">{view?.publicVisible ? 'On' : 'Off'}</span>
        </button>
      </div>
      {loadError ? <p className="text-sm text-[var(--color-error-text)]">{loadError}</p> : null}
      {formError ? (
        <p className="text-sm font-medium text-[var(--color-error-text)]" role="alert">
          {formError}
        </p>
      ) : null}
    </section>
  );
}

export function round2GenderLabel(gender: ShooterGender | null): string {
  return gender ? SHOOTER_GENDER_LABELS[gender] : 'Not set';
}
